import { ViewIds } from '@ardoq/api-types';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { ComponentType, useRef } from 'react';
import {
  buildWorkspaceModeViewModelSourceData,
  getInitialWorkspaceModeSourceDataState,
  WorkspaceModeTimelineModelResetParameters,
  WorkspaceModeTimelineSourceDataArgs,
} from './sourceData/buildWorkspaceModeViewModelSourceData';
import {
  TimelineRenderingLayerStaticProps,
  TimelineRenderingLayerViewSettingsProps,
  TimelineViewCommands,
  ViewModelSourceData,
  buildSettingsControlsForWorkspaceMode,
  defaultTimelineViewCommands,
} from '@ardoq/timeline2024';
import { WorkspaceModeTimelineSourceDataLayer } from './WorkspaceModeTimelineSourceDataLayer';
import { shallowEqual } from 'utils/objectUtils';
import type { TimelineSettingsControlsDependencies } from '@ardoq/timeline2024';
import { updateSettingsBarData } from '@ardoq/view-settings';
import { ViewLegendComponent } from './ViewLegendComponent';
import { EmptyState as EmptyStateBase } from './EmptyState';
import {
  getTimelineViewSettings$,
  getUiOnlyViewSettings$,
} from './viewModel/viewSettingsUtils';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  scan,
} from 'rxjs';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { components$ } from 'streams/components/components$';
import { context$ } from 'streams/context/context$';
import { graphModel$ } from 'modelInterface/graphModel$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { activeFilter$ } from 'streams/filters/activeFilter$';
import { groupingRules$ } from 'streams/grouping/groupingRules$';
import { fields$ } from 'streams/fields/fields$';
import { tags$ } from 'streams/tags/tags$';
import { persistentWorkspaces$ } from 'streams/workspaces/workspaces$';

import { errorInfoBoxLabels$ } from './errorInfoBoxLabels$';
import { startTimelineTracking } from './performanceTrackingUtils';
import { isGraphRelevant } from './sourceData/isGraphRelevant';
const updateViewSettingsDataForWorkspaceMode = (
  settingsData: TimelineSettingsControlsDependencies
) => {
  if (!settingsData.viewInstanceId) {
    return;
  }
  dispatchAction(
    updateSettingsBarData({
      viewId: ViewIds.TIMELINE,
      settingsControlsDependencies: settingsData,
    }),
    settingsData.viewInstanceId
  );
};

const workspaceModeSettingsControls = {
  getSettingsControlsData: buildSettingsControlsForWorkspaceMode,
  publishSettingsControlsData: updateViewSettingsDataForWorkspaceMode,
};

type WorkspaceModeTimelineViewProps = {
  viewInstanceId: string;
  viewSettingsWithChanges$: Observable<
    WorkspaceModeInstanceViewSettings<ViewIds.TIMELINE>
  >;
};
/**
 * The build reads this state through the model interfaces, so it is combined
 * here only to recompute the source data when any of it changes.
 */
const modelResetParameters$: Observable<WorkspaceModeTimelineModelResetParameters> =
  combineLatest({
    activeFilterState: activeFilter$,
    groupingRules: groupingRules$,
    fields: fields$,
    tags: tags$,
    workspaces: persistentWorkspaces$,
  }).pipe(distinctUntilChanged(shallowEqual));

type SourceDataLayerStreamedProps = TimelineRenderingLayerViewSettingsProps & {
  sourceData: ViewModelSourceData;
};

type SourceDataState = {
  sourceDataArgs: WorkspaceModeTimelineSourceDataArgs | null;
  sourceData: ViewModelSourceData;
};

const updateSourceData = (
  { sourceDataArgs: previousSourceDataArgs, sourceData }: SourceDataState,
  sourceDataArgs: WorkspaceModeTimelineSourceDataArgs
): SourceDataState => {
  if (isGraphRelevant(previousSourceDataArgs, sourceDataArgs)) {
    return {
      sourceDataArgs,
      sourceData: buildWorkspaceModeViewModelSourceData(sourceData, {
        ...sourceDataArgs,
        trackingId: startTimelineTracking(),
      }),
    };
  }
  return {
    sourceDataArgs,
    sourceData: {
      ...sourceData,
      sourceDataViewSettings: sourceDataArgs.viewSettings,
    },
  };
};

const toSourceData = ({ sourceData }: SourceDataState) => sourceData;

const getSourceDataLayerProps$ = ({
  viewInstanceId,
  viewSettingsWithChanges$,
}: WorkspaceModeTimelineViewProps): Observable<SourceDataLayerStreamedProps> => {
  const sourceData$: Observable<ViewModelSourceData> = combineLatest({
    context: context$,
    viewSettings: getTimelineViewSettings$(viewSettingsWithChanges$),
    graphModel: graphModel$,
    locale: locale$,
    i18nLocale: i18nLocale$,
    errorInfoBoxLabels: errorInfoBoxLabels$,
    childrenByParent: components$.pipe(
      map(({ childrenByParent }) => childrenByParent)
    ),
    modelResetParameters: modelResetParameters$,
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    distinctUntilChanged(shallowEqual),
    scan(updateSourceData, {
      sourceDataArgs: null,
      sourceData: getInitialWorkspaceModeSourceDataState(viewInstanceId),
    }),
    map(toSourceData)
  );

  return combineLatest({
    sourceData: sourceData$,
    uiOnlyViewSettings: getUiOnlyViewSettings$(viewSettingsWithChanges$),
  });
};

const WorkspaceModeTimelineView = (props: WorkspaceModeTimelineViewProps) => {
  const connectedComponentRef = useRef<ComponentType<
    TimelineRenderingLayerStaticProps & { commands: TimelineViewCommands }
  > | null>(null);
  if (!connectedComponentRef.current) {
    connectedComponentRef.current = connect(
      WorkspaceModeTimelineSourceDataLayer,
      getSourceDataLayerProps$(props)
    );
  }
  const ConnectedSourceDataLayer = connectedComponentRef.current;

  return (
    <ConnectedSourceDataLayer
      key={props.viewInstanceId}
      viewInstanceId={props.viewInstanceId}
      settingsControlsHandlers={workspaceModeSettingsControls}
      commands={defaultTimelineViewCommands}
      ViewLegendComponent={ViewLegendComponent}
      EmptyStateComponent={EmptyStateBase}
    />
  );
};

export { WorkspaceModeTimelineView };
